import { useId, useState, type FC } from 'react';
import { Alert, Button, Dialog, Textarea } from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import { useMarkShipmentDelayed } from './api/mutations';

/*
 * Пометка задержки - DelayModal макета: причина обязательна, метка видна в
 * карточке и списках. Тем же окном меняется причина у уже задержанной отгрузки:
 * момент задержки при этом не сдвигается (решает сервер).
 */

export interface DelayShipmentDialogProps {
  shipmentId: string;
  shipmentNumber: number | string;
  /** Текущая причина - у уже задержанной отгрузки. */
  currentReason?: string | null;
  onClose: () => void;
}

export const DelayShipmentDialog: FC<DelayShipmentDialogProps> = ({
  shipmentId,
  shipmentNumber,
  currentReason,
  onClose,
}) => {
  const reasonId = useId();
  const [reason, setReason] = useState(currentReason ?? '');
  const markDelayed = useMarkShipmentDelayed();
  const isChange = currentReason != null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        isChange
          ? `Причина задержки отгрузки № ${shipmentNumber}`
          : `Пометить отгрузку № ${shipmentNumber} задержанной`
      }
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            disabled={reason.trim() === '' || markDelayed.isPending}
            onClick={() =>
              markDelayed.mutate(
                { path: { id: shipmentId }, body: { reason: reason.trim() } },
                { onSuccess: onClose },
              )
            }
          >
            {isChange ? 'Сохранить причину' : 'Пометить'}
          </Button>
        </>
      }
    >
      <p className="m-0 mb-3 text-[13px] text-ink-600">
        Метка появится в карточке и в списке отгрузок; снимается вручную или
        сама, когда отгрузка закроется.
      </p>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={reasonId} className="text-xs font-medium text-ink-700">
          Причина задержки
        </label>
        <Textarea
          id={reasonId}
          placeholder="Клиент не отвечает, ждём поставку на склад..."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      {markDelayed.isError && (
        <Alert tone="error" className="mt-3.5">
          Не удалось сохранить: {resolveApiErrorMessage(markDelayed.error)}
        </Alert>
      )}
    </Dialog>
  );
};
